import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import MobileBottomNav from "../../components/feature/MobileBottomNav";
import { getBanks, initiateSell, getSavedBanks, addSavedBank, removeSavedBank, verifyAccount } from "../../api/apiServices";
import jsPDF from "jspdf";
import useAuthStatus from "../../hooks/useAuthStatus";
import { useAssets } from "../../hooks/useAssets";
import { useWallet } from "../../hooks/useWallet";
import { toast } from "react-toastify";
import { useTradingConfig } from "../../hooks/useTradingConfig";
import ActionGate from "../../components/feature/ActionGate";
import PageLoader from "../../components/PageLoader";
import { useServices } from "../../hooks/useServices";
import ServiceGate from "../../components/feature/ServiceGate";

const NETWORK_LABELS = {
  bsc:  "BNB Smart Chain (BEP20)",
  eth:  "Ethereum (ERC20)",
  ton:  "TON Network",
  sol:  "Solana",
  tron: "TRON (TRC20)",
};
const netLabel = (n) => NETWORK_LABELS[n?.toLowerCase()] || n?.toUpperCase() || n;

export default function SellCrypto() {
  const { services } = useServices();
  const navigate = useNavigate();
  const isAuthenticated = useAuthStatus();

  const [step, setStep] = useState("select");
  const [selectedSymbol, setSelectedSymbol] = useState(null);
  const [selectedNetwork, setSelectedNetwork] = useState(null);
  const [cryptoInput, setCryptoInput] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [savedBanks, setSavedBanks] = useState([]);
  const [selectedBank, setSelectedBank] = useState(null);
  const [bankMode, setBankMode] = useState("select");
  const [allBanks, setAllBanks] = useState([]);
  const [newBank, setNewBank] = useState({ bankCode: "", bankName: "", accountNumber: "", accountName: "" });
  const [verifying, setVerifying] = useState(false);
  const [savingBank, setSavingBank] = useState(false);

  const { fees, config } = useTradingConfig({ enabled: isAuthenticated });
  const { priceMap, loading: assetsLoading } = useAssets();
  const { holdings, loading: walletLoading } = useWallet({ enabled: isAuthenticated, priceMap });
  const loading = assetsLoading || walletLoading;

  // Sync selectedHolding on price refresh
  const selectedHolding = useMemo(
    () => holdings.find(h => h.symbol === selectedSymbol) || null,
    [holdings, selectedSymbol]
  );

  useEffect(() => {
    if (!isAuthenticated) return;
    getSavedBanks().then(res => { if (res.status) setSavedBanks(res.data); });
    getBanks().then(res => { if (res.status) setAllBanks(res.data); });
  }, [isAuthenticated]);

  const selectedAssetRate = priceMap[selectedHolding?.symbol]?.priceNgn || 0;

  // Balance available on the chosen network. For single-network
  // holdings this equals totalBalance. For multi-network (e.g. USDT
  // on BSC + ETH) it is the balance on the specific network selected.
  const networkBalance = (() => {
    if (!selectedHolding) return 0;
    if (!selectedNetwork || selectedHolding.networks.length === 1)
      return selectedHolding.totalBalance;
    const net = selectedHolding.networks.find(n => n.network === selectedNetwork);
    return net?.balance || 0;
  })();

  const feePercent = fees.sellFeePercent || 2.5;

  const grossNGN   = cryptoInput && selectedAssetRate ? parseFloat(cryptoInput) * selectedAssetRate : 0;
  const fee        = grossNGN * (feePercent / 100);
  const nairaValue = (grossNGN - fee).toFixed(2);

  const handleVerifyNewBank = async () => {
    if (newBank.accountNumber.length !== 10 || !newBank.bankCode) return;
    setVerifying(true);
    try {
      const res = await verifyAccount(newBank.accountNumber, newBank.bankCode);
      if (res.status) {
        setNewBank(prev => ({ ...prev, accountName: res.data.account_name }));
      } else {
        toast.error(res.message || "Could not verify account");
      }
    } catch {
      toast.error("Verification failed");
    } finally {
      setVerifying(false);
    }
  };

  const handleSaveNewBank = async () => {
    setSavingBank(true);
    try {
      const res = await addSavedBank(newBank);
      if (res.status) {
        setSavedBanks(prev => [...prev, res.data]);
        setSelectedBank(res.data);
        setBankMode("select");
        setNewBank({ bankCode: "", bankName: "", accountNumber: "", accountName: "" });
        toast.success("Bank account saved!");
      } else {
        toast.error(res.message || "Failed to save bank");
      }
    } catch {
      toast.error("Something went wrong");
    } finally {
      setSavingBank(false);
    }
  };

  const handleRemoveBank = async (accountNumber) => {
    try {
      await removeSavedBank(accountNumber);
      setSavedBanks(prev => prev.filter(b => b.accountNumber !== accountNumber));
      if (selectedBank?.accountNumber === accountNumber) setSelectedBank(null);
      toast.success("Bank removed");
    } catch {
      toast.error("Failed to remove bank");
    }
  };

  const handleConfirm = async () => {
    if (!selectedHolding || !cryptoInput || !selectedBank || !selectedNetwork) return;
    setSubmitting(true);
    try {
      const payload = {
        assetSymbol:   selectedHolding.symbol,
        cryptoAmount:  parseFloat(cryptoInput),
        network:       selectedNetwork,
        bankName:      selectedBank.bankName,
        bankCode:      selectedBank.bankCode,
        accountNumber: selectedBank.accountNumber,
        accountName:   selectedBank.accountName,
      };
      const response = await initiateSell(payload);
      if (response.status === true) {
        const sellRef = response.data.paystackReference || response.data.reference;
        navigate(`/sell-crypto?reference=${sellRef}`);
      } else {
        toast.error(response.message || "Failed to initiate sell");
      }
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const downloadReceipt = () => {
    const doc = new jsPDF();
    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.text("Nivora - Sell Receipt", 105, 20, { align: "center" });
    doc.setFontSize(12);
    doc.text(`Date: ${new Date().toLocaleDateString()}`, 20, 40);
    doc.text(`Sold: ${cryptoInput} ${selectedHolding.symbol}`, 20, 50);
    doc.text(`Received: ₦${parseFloat(nairaValue).toLocaleString()}`, 20, 60);
    doc.text(`Bank: ${selectedBank?.bankName}`, 20, 70);
    doc.text(`Account: ${selectedBank?.accountNumber}`, 20, 78);
    doc.setFont("helvetica", "normal");
    doc.text("Thank you for using Nivora!", 20, 100);
    doc.save(`Nivora_Sell_${selectedHolding.symbol}.pdf`);
  };

  const goBack = () => {
    if (step === "select") navigate("/home");
    else if (step === "amount") setStep("select");
    else if (step === "bank") { setBankMode("select"); setStep("amount"); }
    else if (step === "confirm") setStep("bank");
    else setStep("select");
  };

  const inputCls = "w-full bg-surface-muted border border-border-strong rounded-xl py-4 px-4 text-primary text-sm placeholder:text-faint focus:outline-none focus:border-brand focus:bg-surface transition-colors";

  if (loading) return <PageLoader size="md" message="Sell crypto loading..." />

  return (
    <ServiceGate serviceKey="trading" services={services} fullPage>
      <div className="min-h-screen bg-bg pb-28">
      <div className="bg-surface px-5 pt-12 pb-4 flex items-center gap-3 border-b border-border">
        <button onClick={goBack} className="w-9 h-9 rounded-xl bg-surface-hover flex items-center justify-center cursor-pointer hover:opacity-80">
          <i className="ri-arrow-left-line text-secondary text-lg"></i>
        </button>
        <h1 className="text-primary font-bold text-lg font-display">Sell Crypto</h1>
      </div>

      <div className="px-5 py-4">
        {/* Progress bar */}
        <div className="flex gap-1.5 mb-6">
          {["Select", "Amount", "Bank", "Confirm"].map((s, i) => {
            const idx = step === "select" ? 0 : step === "amount" ? 1 : step === "bank" ? 2 : 3;
            return <div key={s} className="flex-1 h-1.5 rounded-full transition-all" style={{ background: i <= idx ? "var(--color-brand)" : "var(--color-border-strong)" }} />;
          })}
        </div>

        {/* ── SELECT ASSET ── */}
        {step === "select" && (
          <div>
            <p className="text-secondary text-sm mb-4">Select a crypto asset to sell</p>
            {holdings.length > 0 ? (
              <div className="space-y-2">
                {holdings.map(h => (
                  <button key={h.symbol} onClick={() => {
                    setSelectedSymbol(h.symbol);
                    setSelectedNetwork(h.networks.length === 1 ? h.networks[0].network : null);
                    setCryptoInput("");
                    setStep("amount");
                  }}
                    className="w-full bg-surface border border-border-strong rounded-2xl px-4 py-4 flex items-center justify-between cursor-pointer hover:border-brand-border hover:bg-brand-light/30 transition-all">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full flex items-center justify-center overflow-hidden">
                        {h.image ? <img src={h.image} alt={h.symbol} className="w-7 h-7 object-contain" />
                          : <span className="text-xs font-bold" style={{ color: h.color }}>{h.symbol.charAt(0)}</span>}
                      </div>
                      <div className="text-left">
                        <p className="text-primary text-sm font-bold">{h.symbol}</p>
                        <p className="text-muted text-xs">Balance: {h.totalBalance} {h.symbol}</p>
                      </div>
                    </div>
                    <p className="text-primary text-sm font-semibold">₦{h.valueNGN.toLocaleString()}</p>
                  </button>
                ))}
              </div>
            ) : (
              <div className="bg-surface rounded-3xl p-10 text-center">
                <i className="ri-wallet-3-line text-6xl text-faint mb-4"></i>
                <h3 className="text-xl font-semibold text-primary mb-2 font-display">No Holdings Yet</h3>
                <p className="text-secondary mb-8">You don't have any crypto to sell.</p>
                <div className="flex flex-col gap-3">
                  <button onClick={() => navigate("/deposit")} className="w-full py-4 bg-brand text-on-brand rounded-2xl font-semibold flex items-center justify-center gap-2">
                    <i className="ri-arrow-down-circle-line"></i> Deposit Crypto
                  </button>
                  <button onClick={() => navigate("/buy")} className="w-full py-4 border border-brand-border text-brand rounded-2xl font-semibold flex items-center justify-center gap-2 hover:bg-brand-light">
                    <i className="ri-shopping-cart-line"></i> Buy Crypto
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── AMOUNT ── */}
        {step === "amount" && selectedHolding && (
          <div>
            <div className="flex items-center gap-2 mb-5 bg-brand-light border border-brand-border rounded-xl px-3 py-2 w-fit">
              <div className="w-5 h-5 rounded-full flex items-center justify-center" style={{ background: `${selectedHolding.color}20` }}>
                <i className={`${selectedHolding.icon} text-xs`} style={{ color: selectedHolding.color }}></i>
              </div>
              <span className="text-secondary text-sm font-semibold">{selectedHolding.symbol}</span>
              <button onClick={() => setStep("select")} className="text-brand text-xs cursor-pointer ml-1 font-medium">Change</button>
            </div>

            {selectedHolding.networks.length > 1 && (
              <div className="mb-5">
                <p className="text-secondary text-xs font-medium mb-2">
                  Sell from which network? <span style={{ color: "var(--color-danger)" }}>*</span>
                </p>
                <div className="flex gap-2 flex-wrap">
                  {selectedHolding.networks.map(n => (
                    <button key={n.network} onClick={() => setSelectedNetwork(n.network)}
                      className="px-4 py-2.5 rounded-xl border text-xs font-bold transition-all text-left"
                      style={{
                        background:  selectedNetwork === n.network ? "var(--color-brand-light)" : "var(--color-surface)",
                        borderColor: selectedNetwork === n.network ? "var(--color-brand)"   : "var(--color-border-strong)",
                        color:       selectedNetwork === n.network ? "var(--color-brand-text)" : "var(--color-text-muted)",
                      }}>
                      <span className="block">{netLabel(n.network)}</span>
                      <span className="font-normal text-[10px]">{n.balance} {selectedHolding.symbol}</span>
                    </button>
                  ))}
                </div>
                {!selectedNetwork && (
                  <p className="text-xs mt-1.5 flex items-center gap-1" style={{ color: "var(--color-warning)" }}>
                    <i className="ri-information-line"></i> Select a network to continue
                  </p>
                )}
              </div>
            )}

            <div className="bg-surface border border-border-strong rounded-2xl p-5 mb-3">
              <p className="text-faint text-xs mb-2 font-medium">You Sell ({selectedHolding.symbol})</p>
              <div className="flex items-center gap-2">
                <input type="number" value={cryptoInput} onChange={e => setCryptoInput(e.target.value)}
                  placeholder="0.00000000"
                  className="flex-1 bg-transparent text-primary text-3xl font-bold placeholder:text-faint focus:outline-none font-display" />
                <button onClick={() => setCryptoInput(String(networkBalance))}
                  className="text-brand text-xs cursor-pointer whitespace-nowrap font-semibold bg-brand-light px-2 py-1 rounded-lg">MAX</button>
              </div>
              <p className="text-faint text-xs mt-1">
                Available{selectedHolding.networks.length > 1 && selectedNetwork ? ` on ${netLabel(selectedNetwork)}` : ""}: {networkBalance} {selectedHolding.symbol}
              </p>
              {cryptoInput && parseFloat(cryptoInput) > networkBalance && (
                <p className="text-xs mt-1" style={{ color: "var(--color-danger)" }}>Insufficient balance{selectedNetwork ? ` on ${netLabel(selectedNetwork)}` : ""}</p>
              )}
            </div>

            <div className="bg-surface border border-border-strong rounded-2xl p-5 mb-4">
              <p className="text-faint text-xs mb-2 font-medium">You Receive (NGN)</p>
              <div className="flex items-center gap-2">
                <span className="text-2xl font-bold" style={{ color: "var(--color-brand)" }}>₦</span>
                <span className="text-primary text-3xl font-bold font-display">
                  {parseFloat(nairaValue).toLocaleString()}
                </span>
              </div>
            </div>

            <div className="bg-surface-muted border border-border rounded-xl px-4 py-3 space-y-2 mb-6">
              <div className="flex justify-between text-xs">
                <span className="text-faint">Rate</span>
                <span className="text-secondary">1 {selectedHolding.symbol} = ₦{selectedAssetRate.toLocaleString()}</span>
              </div>
              {selectedNetwork && (
                <div className="flex justify-between text-xs">
                  <span className="text-faint">Network</span>
                  <span className="text-secondary">{netLabel(selectedNetwork)}</span>
                </div>
              )}
              <div className="flex justify-between text-xs">
                <span className="text-faint">Fee ({feePercent}%)</span>
                <span className="text-secondary">₦{parseFloat(fee).toLocaleString()}</span>
              </div>
            </div>

            <ActionGate action="sell" config={config}>
              <button
              onClick={() => {
                const valid = cryptoInput &&
                  parseFloat(cryptoInput) > 0 &&
                  parseFloat(cryptoInput) <= networkBalance &&
                  selectedNetwork;
                if (valid) setStep("bank");
              }}
              disabled={!cryptoInput || parseFloat(cryptoInput) <= 0 || parseFloat(cryptoInput) > networkBalance || !selectedNetwork}
              className="w-full py-4 rounded-2xl text-on-brand font-semibold text-base transition-all active:scale-95 disabled:opacity-40 bg-brand hover:bg-brand-hover">
                Continue
              </button>
            </ActionGate>

          </div>
        )}

        {/* ── BANK STEP ── */}
        {step === "bank" && (
          <div>
            <h2 className="text-primary font-bold text-lg mb-1 font-display">
              {bankMode === "add" ? "Add Bank Account" : "Select Bank Account"}
            </h2>
            <p className="text-faint text-sm mb-6">
              {bankMode === "add" ? "Verify and save your bank account" : "Where should we send your Naira?"}
            </p>

            {bankMode === "select" && (
              <div className="space-y-3">
                {savedBanks.length === 0 && (
                  <div className="bg-surface-muted border border-dashed border-border-strong rounded-2xl p-6 text-center">
                    <i className="ri-bank-line text-3xl text-faint mb-2"></i>
                    <p className="text-muted text-sm">No saved banks yet</p>
                  </div>
                )}

                {savedBanks.map(bank => (
                  <div key={bank.accountNumber}
                    onClick={() => setSelectedBank(bank)}
                    className="relative bg-surface border rounded-2xl px-4 py-4 flex items-center gap-3 cursor-pointer transition-all"
                    style={{
                      borderColor: selectedBank?.accountNumber === bank.accountNumber ? "var(--color-brand)" : "var(--color-border-strong)",
                      background:  selectedBank?.accountNumber === bank.accountNumber ? "var(--color-brand-light)" : "var(--color-surface)",
                    }}
                  >
                    <div className="w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all"
                      style={{ borderColor: selectedBank?.accountNumber === bank.accountNumber ? "var(--color-brand)" : "var(--color-border-strong)" }}>
                      {selectedBank?.accountNumber === bank.accountNumber && (
                        <div className="w-2.5 h-2.5 rounded-full" style={{ background: "var(--color-brand)" }} />
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <p className="text-primary text-sm font-bold truncate">{bank.accountName}</p>
                      <p className="text-muted text-xs">{bank.bankName} · {bank.accountNumber}</p>
                    </div>

                    <button
                      onClick={e => { e.stopPropagation(); handleRemoveBank(bank.accountNumber); }}
                      className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-danger-light text-faint hover:text-danger transition-colors flex-shrink-0"
                    >
                      <i className="ri-delete-bin-line text-sm"></i>
                    </button>
                  </div>
                ))}

                <button
                  onClick={() => setBankMode("add")}
                  className="w-full border border-dashed border-brand-border bg-brand-light/50 rounded-2xl px-4 py-4 flex items-center justify-center gap-2 text-brand text-sm font-semibold hover:bg-brand-light transition-colors"
                >
                  <i className="ri-add-circle-line text-lg"></i>
                  Add New Bank Account
                </button>

                <button
                  onClick={() => selectedBank ? setStep("confirm") : undefined}
                  disabled={!selectedBank}
                  className="w-full mt-2 py-4 rounded-2xl text-on-brand font-semibold text-base transition-all active:scale-95 disabled:opacity-40 bg-brand hover:bg-brand-hover"
                >
                  Continue
                </button>
              </div>
            )}

            {bankMode === "add" && (
              <div className="space-y-4">
                <div>
                  <label className="text-secondary text-xs font-medium mb-2 block">Select Bank</label>
                  <select
                    value={newBank.bankCode}
                    onChange={e => {
                      const selected = allBanks.find(b => b.code === e.target.value);
                      setNewBank(prev => ({ ...prev, bankCode: selected?.code || "", bankName: selected?.name || "", accountName: "" }));
                    }}
                    className={inputCls + " appearance-none"}
                  >
                    <option value="">-- Select your bank --</option>
                    {allBanks.map(bank => <option key={bank.code} value={bank.code}>{bank.name}</option>)}
                  </select>
                </div>

                <div>
                  <label className="text-secondary text-xs font-medium mb-2 block">Account Number</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newBank.accountNumber}
                      onChange={e => setNewBank(prev => ({ ...prev, accountNumber: e.target.value, accountName: "" }))}
                      maxLength={10}
                      placeholder="10-digit account number"
                      className="flex-1 bg-surface-muted border border-border-strong rounded-xl py-4 px-4 text-primary text-sm placeholder:text-faint focus:outline-none focus:border-brand focus:bg-surface transition-colors"
                    />
                    <button
                      onClick={handleVerifyNewBank}
                      disabled={newBank.accountNumber.length !== 10 || !newBank.bankCode || verifying}
                      className="px-4 rounded-xl text-on-brand text-sm font-semibold whitespace-nowrap disabled:opacity-40 cursor-pointer bg-brand hover:bg-brand-hover"
                    >
                      {verifying ? <i className="ri-loader-4-line animate-spin"></i> : "Verify"}
                    </button>
                  </div>

                  {newBank.accountName && (
                    <div className="flex items-center gap-2 mt-2 bg-success-light border border-success-border rounded-xl px-3 py-2">
                      <i className="ri-check-line text-sm" style={{ color: "var(--color-success)" }}></i>
                      <span className="text-xs font-semibold" style={{ color: "var(--color-success-text)" }}>{newBank.accountName}</span>
                    </div>
                  )}
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    onClick={() => { setBankMode("select"); setNewBank({ bankCode: "", bankName: "", accountNumber: "", accountName: "" }); }}
                    className="flex-1 py-4 rounded-2xl border border-border-strong text-secondary font-semibold text-sm hover:bg-surface-hover"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveNewBank}
                    disabled={!newBank.accountName || savingBank}
                    className="flex-1 py-4 rounded-2xl text-on-brand font-semibold text-sm disabled:opacity-40 bg-brand hover:bg-brand-hover"
                  >
                    {savingBank ? "Saving..." : "Save & Use"}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── CONFIRM ── */}
        {step === "confirm" && selectedBank && (
          <div>
            <h2 className="text-primary text-xl font-bold mb-1 font-display">Confirm Sale</h2>
            <p className="text-faint text-sm mb-6">Review before proceeding</p>
            <div className="bg-surface border border-border-strong rounded-2xl p-5 space-y-4 mb-6">
              <div className="flex justify-between"><span className="text-faint text-sm">You Sell</span><span className="text-primary text-sm font-bold">{cryptoInput} {selectedHolding.symbol}</span></div>
              <div className="flex justify-between"><span className="text-faint text-sm">Network</span><span className="text-primary text-sm font-bold">{netLabel(selectedNetwork)}</span></div>
              <div className="flex justify-between"><span className="text-faint text-sm">You Receive</span><span className="text-sm font-bold" style={{ color: "var(--color-brand)" }}>₦{parseFloat(nairaValue).toLocaleString()}</span></div>
              <div className="flex justify-between"><span className="text-faint text-sm">Fee</span><span className="text-secondary text-sm">₦{parseFloat(fee).toLocaleString()}</span></div>
              <div className="border-t border-border pt-3 space-y-2">
                <div className="flex justify-between"><span className="text-faint text-sm">Bank</span><span className="text-secondary text-sm">{selectedBank.bankName}</span></div>
                <div className="flex justify-between"><span className="text-faint text-sm">Account</span><span className="text-secondary text-sm">{selectedBank.accountNumber}</span></div>
                <div className="flex justify-between"><span className="text-faint text-sm">Name</span><span className="text-sm font-semibold" style={{ color: "var(--color-brand)" }}>{selectedBank.accountName}</span></div>
              </div>
            </div>
            <button onClick={handleConfirm} disabled={submitting}
              className="w-full py-4 rounded-2xl text-on-brand font-semibold text-base transition-all active:scale-95 disabled:opacity-40 bg-brand hover:bg-brand-hover">
              {submitting ? "Processing..." : "Confirm & Sell"}
            </button>
          </div>
        )}

        {/* ── PROCESSING ── */}
        {step === "processing" && (
          <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
            <div className="w-20 h-20 rounded-full flex items-center justify-center mb-6" style={{ background: "var(--color-brand-light)" }}>
              <i className="ri-loader-4-line text-4xl animate-spin" style={{ color: "var(--color-brand)" }}></i>
            </div>
            <h2 className="text-primary text-xl font-bold mb-2 font-display">Processing Sale</h2>
            <p className="text-faint text-sm">Converting {selectedHolding.symbol} and sending ₦{parseFloat(nairaValue).toLocaleString()} to your bank...</p>
          </div>
        )}

        {/* ── SUCCESS ── */}
        {step === "success" && (
          <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
            <div className="w-24 h-24 rounded-full border-4 flex items-center justify-center mb-6" style={{ background: "var(--color-brand-light)", borderColor: "var(--color-brand-border)" }}>
              <i className="ri-check-line text-6xl" style={{ color: "var(--color-brand)" }}></i>
            </div>
            <h2 className="text-3xl font-bold mb-2 font-display" style={{ color: "var(--color-brand)" }}>Sale Initiated!</h2>
            <p className="text-secondary mb-8">Your request has been submitted. Naira will be sent to your bank shortly.</p>
            <button onClick={downloadReceipt} className="w-full max-w-xs py-4 border border-brand-border text-brand rounded-2xl font-semibold mb-3">Download Receipt</button>
            <button onClick={() => navigate("/home")} className="w-full max-w-xs py-4 bg-brand text-on-brand rounded-2xl font-semibold">Back to Home</button>
          </div>
        )}
      </div>
      <MobileBottomNav />
    </div>
    </ServiceGate>

  );
}
